import {
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  Button,
} from "@chakra-ui/react";
import { useLoading } from "../hooks/form";
import { useCallback, useEffect, useState } from "react";
import actions from "../../actions";
import { KYDBatchPayout } from "@common/interfaces/KYDOrganization";
import { useDialog, useDialogWithOptions } from "../../Common/Dialog";
import PayoutsTable from "./PayoutsTable";
import { FiFilter } from "react-icons/fi";
import FilterDialog, {
  FilterDialogContext,
  withFilterDialog,
} from "./FilterDialog";
import { PaymentReportPeriod, Period } from "@common/interfaces/KYDMetrics";
import dayjs from "dayjs";

const PayoutsDashboard = () => {
  const breakpointValue = useBreakpointValue({ base: "xs", lg: "sm" });
  const [loading, updateLoading] = useLoading({
    init: true,
  });
  const filterDialog = useDialogWithOptions(FilterDialogContext);
  const [payouts, setPayouts] = useState<KYDBatchPayout[]>([]);
  const dialog = useDialog();
  const [period, setPeriod] = useState<PaymentReportPeriod>("all");
  const [range, setRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dayjs().subtract(1, "month").toDate(),
    endDate: new Date(),
  });
  const fetchPayouts = useCallback(
    async (period: PaymentReportPeriod) => {
      updateLoading("init", true);
      try {
        const _ = await actions.ADMIN.fetchOrganizationPayouts({
          period,
          start_at: dayjs(range.startDate).toISOString(),
          end_at: dayjs(range.endDate).toISOString(),
        });
        setPayouts(_);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
    },
    [updateLoading, dialog, range]
  );

  useEffect(() => {
    fetchPayouts(period);
  }, [fetchPayouts, period]);

  return (
    <Stack spacing={{ base: "4", lg: "6" }}>
      <FilterDialog
        initialPreset={period}
        initialStartDate={range.startDate}
        initialEndDate={range.endDate}
        isLoading={loading.init}
        onSave={({ preset, startDate, endDate }) => {
          setPeriod(preset as PaymentReportPeriod);
          setRange({
            startDate: dayjs(startDate).toDate(),
            endDate: dayjs(endDate).toDate(),
          });
          filterDialog.close();
        }}
        onClose={() => {
          filterDialog.close();
        }}
      />
      {loading["init"] ? (
        <VStack>
          <Spinner />
          <Text>Loading payouts...</Text>
        </VStack>
      ) : (
        <VStack h="full" w="full" spacing="4">
          <HStack spacing="1" width={"full"} justifyContent={"space-between"}>
            <Heading size={breakpointValue} fontWeight="medium">
              Payouts
            </Heading>

            <Button
              variant="ghost"
              py={4}
              px={4}
              gap={"8px"}
              onClick={() => filterDialog.open()}
            >
              <FiFilter />
              Filters
            </Button>
          </HStack>
          <Stack
            h="full"
            w="full"
            flexGrow={1}
            spacing={{ base: "5", lg: "6" }}
          >
            <PayoutsTable payouts={payouts} />
          </Stack>
        </VStack>
      )}
    </Stack>
  );
};

export default withFilterDialog(PayoutsDashboard);
